import { useEffect, useMemo, useRef } from 'react';
import type { Map, GeoJSONSource } from 'mapbox-gl';
import { useMapBox } from './BalloonEventsMapBox';
import { useMapHotspots, EXCLUDED_PROVIDERS } from '../hooks/useMapHotspots';
import { useBalloonEventsMetadata, useQuery } from 'react-ui';
import type { HotspotFeatureCollection } from 'ts-core';

const SOURCE_ID = 'hotspots-source';
const LAYER_CLUSTERS = 'hotspots-clusters';
const LAYER_CLUSTER_COUNT = 'hotspots-cluster-count';
const LAYER_UNCLUSTERED = 'hotspots-unclustered';

const EMPTY_COLLECTION: HotspotFeatureCollection = {
  type: 'FeatureCollection',
  features: [],
};

// All known category colors. Used to track dominant color per cluster via
// clusterProperties, so clusters display the colour of their majority category.
const CATEGORY_COLORS = [
  '#018E87', '#047325', '#367ad3', '#4537E0', '#4681ce',
  '#76B305', '#A21FB4', '#B87F63', '#D13640', '#D6A801',
  '#DC662C', '#ed5fad',
] as const;

/**
 * Recursively builds a Mapbox expression that returns the color key whose
 * clusterProperty count is >= all others. Falls back to the last color.
 */
function dominantColorExpr(colors: readonly string[]): unknown {
  if (colors.length <= 1) return colors[0] ?? '#888888';
  const [head, ...rest] = colors;
  const allGe = rest.map((other) => ['>=', ['get', head], ['get', other]]);
  const condition = allGe.length === 1 ? allGe[0] : ['all', ...allGe];
  return ['case', condition, head, dominantColorExpr(rest)];
}

const clusterColorExpr = [
  'case',
  ['has', 'point_count'],
  dominantColorExpr(CATEGORY_COLORS),
  ['get', 'color'],
] as unknown[];

function addHotspotLayers(map: Map, initialData: HotspotFeatureCollection): void {
  if (!map.getSource(SOURCE_ID)) {
    const clusterProperties = Object.fromEntries(
      CATEGORY_COLORS.map((c) => [
        c,
        ['+', ['case', ['==', ['get', 'color'], c], 1, 0]],
      ])
    );

    map.addSource(SOURCE_ID, {
      type: 'geojson',
      data: initialData,
      cluster: true,
      clusterRadius: 50,
      clusterProperties,
    });
  }

  if (!map.getLayer(LAYER_CLUSTERS)) {
    map.addLayer({
      id: LAYER_CLUSTERS,
      type: 'circle',
      source: SOURCE_ID,
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': clusterColorExpr as mapboxgl.Expression,
        'circle-radius': [
          'interpolate', ['linear'],
          ['get', 'point_count'],
          1,    14,
          10,   20,
          100,  30,
          500,  44,
          2000, 60,
        ],
        'circle-opacity': 0.35,
        'circle-stroke-width': 2,
        'circle-stroke-color': 'rgba(255,255,255,0.4)',
      },
    });
  }

  if (!map.getLayer(LAYER_CLUSTER_COUNT)) {
    map.addLayer({
      id: LAYER_CLUSTER_COUNT,
      type: 'symbol',
      source: SOURCE_ID,
      filter: ['has', 'point_count'],
      layout: {
        'text-field': '{point_count_abbreviated}',
        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
        'text-size': 12,
      },
      paint: {
        'text-color': '#ffffff',
        'text-halo-color': 'rgba(0,0,0,0.25)',
        'text-halo-width': 1,
      },
    });
  }

  if (!map.getLayer(LAYER_UNCLUSTERED)) {
    map.addLayer({
      id: LAYER_UNCLUSTERED,
      type: 'circle',
      source: SOURCE_ID,
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-color': ['get', 'color'],
        'circle-radius': 8,
        'circle-opacity': 0.4,
        'circle-stroke-width': 1.5,
        'circle-stroke-color': 'rgba(255,255,255,0.4)',
      },
    });
  }
}

function removeHotspotLayers(map: Map): void {
  for (const id of [LAYER_CLUSTER_COUNT, LAYER_CLUSTERS, LAYER_UNCLUSTERED]) {
    if (map.getLayer(id)) map.removeLayer(id);
  }
  if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
}

/**
 * Renders Mapbox GL cluster circles showing event density at low zoom levels
 * (< VITE_HOTSPOT_MAX_ZOOM). Individual event points are fetched once on mount
 * and clustered client-side by Mapbox. Returns null — no DOM output.
 */
export function HotspotsLayer() {
  const mapInfo = useMapBox();
  const map = mapInfo?.map ?? null;
  const { metadata } = useBalloonEventsMetadata();
  const { query } = useQuery();
  // Partner-scoped map mode narrows the hotspots to the partner's data-source
  // provider via query.providers; otherwise show every (non-excluded) provider.
  const providers = useMemo(
    () =>
      query.providers && query.providers.length > 0
        ? query.providers
        : metadata.providers.map((p) => p.shortName).filter((s) => !EXCLUDED_PROVIDERS.includes(s)),
    [metadata, query.providers]
  );
  // Only narrow the hotspot fetch by tag when the user has activated a
  // saved-search-bound tag (forceMapClustering is the saved-search opt-in).
  // Without this gate, every casual tag chip would also strip the dot
  // density — surprising in the non-clustering UX.
  const tags = useMemo(
    () => (query.forceMapClustering && query.tags ? query.tags : []),
    [query.forceMapClustering, query.tags]
  );
  // User-scoped map mode: limit hotspot dots to one user's events.
  const user = useMemo(() => query.user ?? [], [query.user]);
  const { data } = useMapHotspots(map, providers, tags, user);

  const dataRef = useRef<HotspotFeatureCollection | null>(null);
  dataRef.current = data;

  useEffect(() => {
    if (!map) return;

    const setup = () => {
      if (!map.isStyleLoaded() || map.getSource(SOURCE_ID)) return;
      addHotspotLayers(map, dataRef.current ?? EMPTY_COLLECTION);
    };

    map.on('idle', setup);
    setup();

    return () => {
      map.off('idle', setup);
      if (map.isStyleLoaded()) {
        removeHotspotLayers(map);
      }
    };
  }, [map]);

  useEffect(() => {
    if (!map) return;
    const source = map.getSource(SOURCE_ID) as GeoJSONSource | undefined;
    if (source) {
      source.setData(data ?? EMPTY_COLLECTION);
    }
  }, [map, data]);

  return null;
}
